import { pageTitle } from '@palaccount/config';
import {
  ADJUSTMENT_REASONS,
  DATA_TRANSFER_MESSAGES,
  DATA_TYPE_WRITE_PERMISSION,
  EXPORT_FORMATS,
  isImportable,
  isPostingImport,
  type AdjustmentReason,
  type DataType,
  type ExportFormat,
  type ImportDuplicateMode,
  type ImportProblem,
  type ImportResult,
  type TransferColumn,
  type TransferGrid,
  type TransferRun,
} from '@palaccount/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CloudUpload, Download, Info, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api } from '@/api';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { NativeSelect } from '@/components/ui/native-select';
import { downloadCsv, downloadXlsx } from '@/features/reports/export';
import { localizeServerCause, localizeServerMessage, throwApiError } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import {
  assertStagedFits,
  assertUnderLimit,
  chunkSize,
  headersToKeys,
  ImportFileError,
  needsStaging,
  readImportFile,
  refusedRowsFile,
} from './import-file';

/**
 * ADM-03 — THE IMPORT / EXPORT CENTRE (§15.8), both halves.
 *
 * §4's two cards side by side, and §8 says why they are not tabs: "a person
 * setting up a company often exports one thing to check it and imports
 * another, and tabs would hide one behind the other."
 *
 * ── THE DROP ZONE IS REAL NOW, AND THAT IS THE WHOLE POINT ─────────────────
 *
 * The export wave left this card without one, deliberately: §6 lists the
 * import as a prototype no-op, and a drop zone that accepts a file and does
 * nothing with it is EC-19-59's defect on the one screen where the cost is a
 * person believing their thousand items are loaded. It is drawn now because
 * the import behind it is real.
 *
 * ── WHAT THE SCREEN OWES BR-ADM-03-01 ─────────────────────────────────────
 *
 * "Show how many rows will be ADDED, how many CHANGED, and how many REFUSED,
 * and WAIT for the user to say go." So dropping a file never imports it. It
 * runs the import on the server and rolls it back, and what comes back is the
 * three counts and every problem — each naming the row and the column, because
 * ERR-ADM-03-01 speaks in those terms and so must the report. The Go button is
 * the only thing that commits, and it re-sends the same file so that what is
 * confirmed is what was checked.
 *
 * ── WHY THE FILE IS BUILT IN THE BROWSER ───────────────────────────────────
 *
 * The server answers with a GRID and the client writes the file, using the
 * same `downloadXlsx`/`downloadCsv` the reports wave already owns. Those know
 * which columns are numbers and which are dates — the two things a CSV cannot
 * express and the two an accountant notices immediately. The parts that must
 * NOT be a client's business are on the server and stay there: which rows,
 * which columns (BR-ADM-03-08), the audit row and the history row
 * (BR-ADM-03-06/-07).
 */

const KEY = ['data-transfer'] as const;

function useTransferOptions() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () => {
      const res = await api.dataTransfer.options();
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
  });
}

/** What was read out of the file, held between the dry run and the go. */
interface StagedFile {
  name: string;
  columns: TransferColumn[];
  /** The header row, resolved to column keys — see `import-file.ts`. */
  keys: string[];
  rows: string[][];
  /**
   * §15's "very large" — the server-side upload this file was chunked into,
   * or null when it fitted in one request.
   *
   * Held across the dry run and the go on purpose: the staged rows ARE the
   * file, so confirming applies exactly the rows that were checked rather than
   * re-uploading and trusting the browser to send the same ones.
   */
  uploadId: string | null;
}

/**
 * §15: "the import must show PROGRESS and must not appear frozen."
 *
 * Every figure here came back from the SERVER — `received` is a `count(*)`
 * over the staged rows — which is the difference between a progress indicator
 * and a spinner with a number next to it. A browser-side "chunks I have sent ×
 * chunk size" agrees with this right up until the moment it does not, and the
 * moment it does not is the only moment a progress bar is worth having.
 */
interface UploadProgressState {
  received: number;
  total: number;
}

export function ImportExportPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending } = useTransferOptions();

  const [importType, setImportType] = useState<DataType | ''>('');
  const [exportType, setExportType] = useState<DataType | ''>('');
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [failure, setFailure] = useState<string | null>(null);

  const [staged, setStaged] = useState<StagedFile | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [onDuplicate, setOnDuplicate] = useState<ImportDuplicateMode>('stop');
  const [progress, setProgress] = useState<UploadProgressState | null>(null);
  /**
   * §12's two POSTING types need a date, and a stock rewrite outside the
   * opening period needs a reason. Both are asked for BEFORE the file is
   * dropped rather than after the dry run, because the dry run's answer
   * depends on them: BR-ADM-03-05 is a question about the period the date
   * falls in, so a preview without one would be a preview of nothing.
   */
  const [asOf, setAsOf] = useState('');
  const [reason, setReason] = useState<AdjustmentReason | ''>('');
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = pageTitle(t('impexp.title'));
  }, [t]);

  // ERR-ADM-03-05 — the dropdown is drawn from what the SERVER says this
  // person may read, never from the six-type constant. A type they cannot see
  // is absent rather than refused on submit.
  const types = data?.dataTypes ?? [];
  // The IMPORT side narrows that twice over: only four of the six can be
  // imported at all (the two that POST cannot — see IMPORTABLE_DATA_TYPES),
  // and importing takes create+edit where exporting takes view.
  //
  // Drawn from what the caller may READ rather than from the server's write
  // list on purpose. PART 16.7 row 2: "the role has View but not the action →
  // show the screen and GREY OUT the button… THE BUTTON MUST NEVER VANISH, or
  // the person cannot tell whether the feature exists." So a role that can
  // download the catalogue and not change it still sees Items here, with the
  // drop zone greyed and a sentence saying why — rather than an import card
  // that silently has nothing in it.
  const importTypes = types.filter(isImportable);
  useEffect(() => {
    if (types.length > 0) setExportType((current) => current || types[0]!);
    if (importTypes.length > 0) setImportType((current) => current || importTypes[0]!);
  }, [types, importTypes]);

  const download = useMutation({
    mutationFn: async (input: { dataType: DataType; format: ExportFormat }) => {
      const res = await api.dataTransfer.exportData({ body: input });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: (grid, input) => {
      writeFile(grid, input.format, t);
      // The history gained a row on the server; redraw it rather than
      // guessing at it.
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
    onError: (error) => setFailure(localizeServerCause(t, error, 'impexp.errSaveFailed')),
  });

  const template = useMutation({
    mutationFn: async (dataType: DataType) => {
      const res = await api.dataTransfer.template({ query: { dataType } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    // §6: the template is always .xlsx — it is a form to fill in, and a CSV
    // template loses the column types the person is being asked to respect.
    onSuccess: (grid) => writeFile(grid, 'xlsx', t, 'template'),
    onError: (error) => setFailure(localizeServerCause(t, error, 'impexp.errSaveFailed')),
  });

  /**
   * The one call that does everything — `confirm: false` checks and rolls
   * back, `confirm: true` commits. One route on both sides, so what the person
   * approved cannot differ from what happens.
   */
  const runImport = useMutation({
    mutationFn: async (input: { file: StagedFile; confirm: boolean; type: DataType }) => {
      const res = await api.dataTransfer.importData({
        body: {
          dataType: input.type,
          // A STAGED file sends neither: the rows are already on the server and
          // so is the header. Sending both would be two sources for one file,
          // which is how a confirmation ends up applying something other than
          // what was checked.
          columns: input.file.uploadId === null ? input.file.keys : [],
          rows: input.file.uploadId === null ? input.file.rows : [],
          uploadId: input.file.uploadId,
          onDuplicate,
          confirm: input.confirm,
          ...(isPostingImport(input.type) ? { asOf } : {}),
          ...(input.type === 'stock_balances' && reason !== '' ? { reason } : {}),
        },
      });
      if (res.status !== 200) throwApiError(res.status, res.body);
      return res.body;
    },
    onSuccess: (report) => {
      setResult(report);
      if (report.applied) {
        // The history gained a row on the server; redraw rather than guess.
        void queryClient.invalidateQueries({ queryKey: KEY });
        setStaged(null);
        setProgress(null);
        if (fileInput.current) fileInput.current.value = '';
      }
    },
    onError: (error) => {
      setFailure(localizeServerCause(t, error, 'impexp.errSaveFailed'));
      setResult(null);
    },
  });

  /**
   * §15's "very large" — the file, chunked into the server's staging table.
   *
   * Returns the upload id the import then names. Nothing here imports
   * anything: staging is not importing, and the person still has to say go
   * (BR-ADM-03-01) after seeing a dry run over the rows that landed.
   */
  async function stageFile(type: DataType, keys: string[], rows: string[][]): Promise<string> {
    const opened = await api.dataTransfer.beginUpload({
      body: { dataType: type, columns: keys, totalRows: rows.length },
    });
    if (opened.status !== 201) throwApiError(opened.status, opened.body);
    const uploadId = opened.body.uploadId;
    setProgress({ received: 0, total: opened.body.total });

    const size = chunkSize(rows);
    for (let from = 0; from < rows.length; from += size) {
      const sent = await api.dataTransfer.uploadChunk({
        body: { uploadId, firstRow: from + 2, rows: rows.slice(from, from + size) },
      });
      if (sent.status !== 200) throwApiError(sent.status, sent.body);
      // The server's own count, every time — see `UploadProgressState`.
      setProgress({ received: sent.body.received, total: sent.body.total });
    }
    return uploadId;
  }

  async function takeFile(file: File | undefined): Promise<void> {
    if (!file || !importType) return;
    setFailure(null);
    setResult(null);
    setStaged(null);
    setProgress(null);
    // §12's date is not optional for the two types that post, and asking after
    // the file has been read would mean reading it twice.
    if (isPostingImport(importType) && asOf === '') {
      // Through the map, not raw. The SERVER raises this same sentence
      // (`data-import.service.ts:394`), so the screen and the refusal must say
      // the identical thing in the reader's language — which is exactly what
      // `localizeServerMessage` guarantees and a hand-written `t()` key would
      // let drift the first time the contract is reworded.
      setFailure(localizeServerMessage(t, DATA_TRANSFER_MESSAGES.dateRequired));
      return;
    }
    try {
      // BR-ADM-03-10 first, before a byte is parsed: a 40 MB file must be
      // refused with a sentence, not turned into a frozen tab.
      assertUnderLimit(file, t);
      const grid = await readImportFile(file, t);
      const [headers = [], ...rows] = grid;
      // §15's ceiling, said before fifty round trips discover it.
      assertStagedFits(rows, t);

      // The template is asked for rather than assumed, because it is what
      // decides which columns this CALLER has (a role without see_item_cost
      // has no cost column) and it is where the labels come from.
      const res = await api.dataTransfer.template({ query: { dataType: importType } });
      if (res.status !== 200) throwApiError(res.status, res.body);
      const keys = headersToKeys(headers, res.body.columns, t);

      // ── ONE REQUEST, OR MANY ──────────────────────────────────────────────
      //
      // A file that fits in one body goes in one body — §13's walkthrough is
      // 400 rows and should not pay for a staging table. Anything larger is
      // chunked, and the two paths differ ONLY in where the import reads its
      // rows from: same route, same `confirm`, same transaction, same
      // all-or-nothing.
      const uploadId = needsStaging(rows) ? await stageFile(importType, keys, rows) : null;

      const file_: StagedFile = {
        name: file.name,
        columns: res.body.columns,
        keys,
        rows,
        uploadId,
      };
      setStaged(file_);
      runImport.mutate({ file: file_, confirm: false, type: importType });
    } catch (error) {
      // TWO KINDS OF FAILURE MEET HERE AND ONLY ONE OF THEM IS THE SERVER'S.
      // `ImportFileError` is thrown by `import-file.ts` before anything is
      // sent — too large, too many rows, unreadable, wrong type — and its
      // message is this screen's own translated sentence. Localizing it again
      // would look up a key that is already the answer. Everything else
      // reached the network, so it goes through the seam: a refusal keeps the
      // server's sentence localized, and a request that never came back gets
      // this screen's sentence instead of the browser's `Failed to fetch`.
      setFailure(
        error instanceof ImportFileError
          ? error.message
          : localizeServerCause(t, error, 'impexp.errSaveFailed'),
      );
      setProgress(null);
    }
  }

  if (isPending) {
    return <p className="py-10 text-center text-sm text-muted-foreground">{t('shell.loading')}</p>;
  }

  return (
    <div className="grid gap-4">
      {failure ? (
        <p role="alert" className="text-sm text-destructive">
          {failure}
        </p>
      ) : null}

      {/* §4: "Two equal cards side by side: Import on the left, Export on the
          right." Stacked on a narrow window — every layout has a narrow-window
          rule (§21.17). */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* ── Import ── */}
        <Card>
          <CardContent className="grid gap-3 p-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Upload className="size-4" aria-hidden />
              {t('impexp.importTitle')}
            </h2>
            <p className="text-xs text-muted-foreground">{t('impexp.importIntro')}</p>

            <label className="grid gap-1 text-xs text-muted-foreground">
              {t('impexp.dataType')}
              <NativeSelect
                value={importType}
                onChange={(event) => {
                  setImportType(event.target.value as DataType);
                  setStaged(null);
                  setResult(null);
                }}
                aria-label={t('impexp.dataTypeImport')}
              >
                {importTypes.map((type) => (
                  <option key={type} value={type}>
                    {t(`impexp.type.${type}`)}
                  </option>
                ))}
              </NativeSelect>
            </label>

            <Button
              type="button"
              variant="outline"
              disabled={!importType || template.isPending}
              onClick={() => importType && template.mutate(importType)}
            >
              <Download className="size-4" aria-hidden />
              {t('impexp.downloadTemplate')}
            </Button>

            {/* §12's two POSTING types, and the two things they need that the
                four master types do not. Shown only for them, because a date
                on an item import would be a field that does nothing — and a
                field that does nothing is a field somebody will believe in. */}
            {importType && isPostingImport(importType) ? (
              <>
                <label className="grid gap-1 text-xs text-muted-foreground">
                  {t('impexp.asOf')}
                  <input
                    type="date"
                    value={asOf}
                    onChange={(event) => {
                      setAsOf(event.target.value);
                      setStaged(null);
                      setResult(null);
                    }}
                    className="rounded-md border border-border bg-transparent px-3 py-1.5 text-sm"
                  />
                  <span className="text-[11px]">{t('impexp.asOfHint')}</span>
                </label>

                {/* §12: "an import of stock balances outside the opening period
                    must be a STOCK ADJUSTMENT WITH A REASON, not a silent
                    rewrite." Optional in the form because INSIDE the opening
                    period the server uses `opening_balance` — and it is the
                    server that knows which of the two the period is, so the
                    screen offers the choice and lets the refusal say when it
                    was needed. */}
                {importType === 'stock_balances' ? (
                  <label className="grid gap-1 text-xs text-muted-foreground">
                    {t('impexp.reason')}
                    <NativeSelect
                      value={reason}
                      onChange={(event) => {
                        setReason(event.target.value as AdjustmentReason | '');
                        setStaged(null);
                        setResult(null);
                      }}
                      aria-label={t('impexp.reason')}
                    >
                      <option value="">{t('impexp.reasonOpening')}</option>
                      {ADJUSTMENT_REASONS.map((option) => (
                        <option key={option} value={option}>
                          {t(`adj.reason.${option}`)}
                        </option>
                      ))}
                    </NativeSelect>
                  </label>
                ) : null}
              </>
            ) : null}

            {/* §5 field 2's drop zone. PART 16.7 row 2 wants the control
                greyed with a reason rather than hidden when the role may look
                at the data and not change it — which is exactly the role an
                export-shaped permission fence would have let import. */}
            {importType ? (
              <Gated
                permission={DATA_TYPE_WRITE_PERMISSION[importType as never][0]!}
                reasonKey="perm.cannot.importRecords"
              >
                <DropZone
                  inputRef={fileInput}
                  busy={runImport.isPending}
                  fileName={staged?.name ?? null}
                  onFile={(file) => void takeFile(file)}
                />
              </Gated>
            ) : (
              <p className="flex items-start gap-2 rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
                <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>{t('impexp.nothingImportable')}</span>
              </p>
            )}

            {/* §5's line, said before somebody goes looking for it: the two
                data types that POST cost more than the four that do not. */}
            <p className="text-[11px] text-muted-foreground">
              {localizeServerMessage(t, DATA_TRANSFER_MESSAGES.importPostingTypes)}
            </p>

            {/* §15: "the import must show PROGRESS and must not appear frozen."
                Both numbers are the SERVER's — see `UploadProgressState`. */}
            {progress && !result ? <UploadMeter progress={progress} /> : null}

            {result ? (
              <ImportReport
                result={result}
                staged={staged}
                onDuplicate={onDuplicate}
                onChooseDuplicate={(mode) => {
                  setOnDuplicate(mode);
                  setResult(null);
                }}
                onConfirm={() =>
                  staged &&
                  importType &&
                  runImport.mutate({ file: staged, confirm: true, type: importType })
                }
                busy={runImport.isPending}
              />
            ) : null}

            <History title={t('impexp.recentImports')} runs={data?.imports ?? []} empty={t('impexp.noImports')} />
          </CardContent>
        </Card>

        {/* ── Export ── */}
        <Card>
          <CardContent className="grid gap-3 p-6">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <Download className="size-4" aria-hidden />
              {t('impexp.exportTitle')}
            </h2>
            <p className="text-xs text-muted-foreground">{t('impexp.exportIntro')}</p>

            <label className="grid gap-1 text-xs text-muted-foreground">
              {t('impexp.dataType')}
              <NativeSelect
                value={exportType}
                onChange={(event) => setExportType(event.target.value as DataType)}
                aria-label={t('impexp.dataTypeExport')}
              >
                {types.map((type) => (
                  <option key={type} value={type}>
                    {t(`impexp.type.${type}`)}
                  </option>
                ))}
              </NativeSelect>
            </label>

            <div className="grid gap-1">
              <span className="text-xs text-muted-foreground">{t('impexp.format')}</span>
              <div className="flex gap-2">
                {EXPORT_FORMATS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => setFormat(option)}
                    aria-pressed={format === option}
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-xs font-medium',
                      format === option
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {option.toUpperCase()}
                  </button>
                ))}
              </div>
              {/* §5 field 3 lists PDF as a third. Said out loud rather than
                  quietly dropped — see the contract for why a 50,000-row PDF
                  is not what "everything in a file" means. */}
              <p className="text-[11px] text-muted-foreground">{t('impexp.noPdf')}</p>
            </div>

            <Button
              type="button"
              disabled={!exportType || download.isPending}
              onClick={() => exportType && download.mutate({ dataType: exportType, format })}
            >
              <Download className="size-4" aria-hidden />
              {t('impexp.exportNow')}
            </Button>

            <History title={t('impexp.recentExports')} runs={data?.exports ?? []} empty={t('impexp.noExports')} />
          </CardContent>
        </Card>
      </div>

      {/* BR-ADM-03-06's other half, said where the person can see it: every run
          here is in the audit log, and DSH-09 is where it is read. */}
      <p className="text-xs text-muted-foreground">
        {t('impexp.auditNote')}{' '}
        <Link to="/reports/audit" className="underline">
          {t('audit.title')}
        </Link>
      </p>
    </div>
  );
}

/**
 * §6's Upload file — "takes ONE file, by clicking or by dragging onto it".
 *
 * A `<label>` wrapping a visually hidden `<input type="file">` rather than a
 * div with a click handler: that way the keyboard reaches it, a screen reader
 * announces it as a file control, and the browser's own picker opens. `<Gated>`
 * makes the whole thing inert when the role may not write, which is why the
 * disabled state here is only the BUSY one — a control greyed for a permission
 * and a control greyed because it is working are different states and must not
 * be the same code (16.7 rows 2 and 3).
 */
function DropZone({
  inputRef,
  busy,
  fileName,
  onFile,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  busy: boolean;
  fileName: string | null;
  onFile: (file: File | undefined) => void;
}) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);

  return (
    <label
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        // "One file at a time — the product does not accept more than one."
        onFile(event.dataTransfer.files[0]);
      }}
      className={cn(
        'grid cursor-pointer justify-items-center gap-1 rounded-md border border-dashed p-6 text-center text-xs',
        over ? 'border-primary bg-primary/5' : 'border-border text-muted-foreground',
      )}
    >
      <CloudUpload className="size-6" aria-hidden />
      <span className="font-medium text-foreground">
        {busy ? t('impexp.checking') : (fileName ?? t('impexp.uploadFile'))}
      </span>
      <span>{t('impexp.uploadHint')}</span>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx"
        aria-label={t('impexp.uploadFile')}
        disabled={busy}
        className="sr-only"
        onChange={(event) => onFile(event.target.files?.[0])}
      />
    </label>
  );
}

/**
 * §15's progress, and it is a MEASUREMENT rather than an animation.
 *
 * "The import must show progress and must not appear frozen." A spinner
 * satisfies neither half — it shows that something is happening, not how much
 * of it is left, and it keeps spinning just as convincingly when nothing is
 * happening at all. Both numbers here came back from the server's own
 * `count(*)` over the rows it has actually stored, so the bar cannot run ahead
 * of the truth.
 *
 * `role="progressbar"` with the real min/max/now, so a screen reader is told
 * the same thing the bar shows rather than being told that something is busy.
 */
function UploadMeter({ progress }: { progress: UploadProgressState }) {
  const { t } = useTranslation();
  const percent = progress.total === 0 ? 0 : Math.round((progress.received / progress.total) * 100);
  return (
    <div className="grid gap-1">
      <p className="text-xs text-muted-foreground">
        {t('impexp.uploading', {
          received: progress.received.toLocaleString(),
          total: progress.total.toLocaleString(),
        })}
      </p>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.received}
        aria-label={t('impexp.uploadProgress')}
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
      >
        <div className="h-full bg-primary transition-all" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

/**
 * BR-ADM-03-01's answer — "how many rows will be added, how many changed, and
 * how many refused", and then it waits.
 *
 * The counts came back from a run that ACTUALLY HAPPENED and was rolled back,
 * so they are not an estimate. Go re-sends the same file with `confirm`, and
 * the server checks it all again — a preview is not a promise, and nothing is
 * held between two requests.
 */
function ImportReport({
  result,
  staged,
  onDuplicate,
  onChooseDuplicate,
  onConfirm,
  busy,
}: {
  result: ImportResult;
  staged: StagedFile | null;
  onDuplicate: ImportDuplicateMode;
  onChooseDuplicate: (mode: ImportDuplicateMode) => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const refused = result.problems.length > 0;

  if (result.applied) {
    return (
      <p role="status" className="rounded-md bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-400">
        {t('impexp.imported', { added: result.added, changed: result.changed })}
      </p>
    );
  }

  return (
    <div className="grid gap-2 rounded-md border border-border p-3 text-xs">
      <p className="font-medium" role="status">
        {t('impexp.counts', {
          added: result.added,
          changed: result.changed,
          refused: result.refused,
        })}
      </p>

      {refused ? (
        <>
          {/* Every problem, not the first — the whole reason the server runs
              the file to the end. Capped in the LIST only; the download below
              carries all of them. */}
          <ul className="grid max-h-48 gap-1 overflow-y-auto">
            {result.problems.slice(0, 50).map((problem, index) => (
              <li key={index} className="text-destructive">
                {describe(problem, t)}
              </li>
            ))}
          </ul>
          {result.problems.length > 50 ? (
            <p className="text-muted-foreground">
              {t('impexp.moreProblems', { count: result.problems.length - 50 })}
            </p>
          ) : null}

          {/* BR-ADM-03-03 — the refused rows, with the reason on each one. */}
          {staged ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const file = refusedRowsFile(staged.columns, staged.rows, result.problems, t);
                downloadCsv(`${result.dataType}-refused.csv`, file.headers, file.rows);
              }}
            >
              <Download className="size-4" aria-hidden />
              {t('impexp.downloadRefused')}
            </Button>
          ) : null}

          {/* ERR-ADM-03-04's own question — "do you want to update them, or
              stop?" — offered only when the answer would change anything. */}
          {onDuplicate === 'stop' && result.problems.some((problem) => problem.row === null) ? (
            <Button type="button" variant="outline" onClick={() => onChooseDuplicate('update')}>
              {t('impexp.chooseUpdate')}
            </Button>
          ) : null}
        </>
      ) : (
        <Button type="button" onClick={onConfirm} disabled={busy}>
          <Upload className="size-4" aria-hidden />
          {t('impexp.go')}
        </Button>
      )}
    </div>
  );
}

/**
 * One problem, in the terms the person's spreadsheet uses.
 *
 * ERR-ADM-03-01 quotes a column POSITION, so the sentence does too — "Row 12,
 * column 3 (Category): …". A problem with no row belongs to the file and says
 * so by carrying no prefix at all.
 */
function describe(problem: ImportProblem, t: TFunction): string {
  if (problem.row === null) return problem.message;
  const column =
    problem.column === null
      ? null
      : t('impexp.atColumn', {
          position: problem.columnPosition ?? 0,
          label: t(`impexp.col.${problem.column}`),
        });
  return `${t('impexp.atRow', { row: problem.row })}${column ? `, ${column}` : ''}: ${problem.message}`;
}

/**
 * §4's two history lists, and §15's Empty row — "no imports yet must show
 * 'No imports yet.' and no exports yet 'No exports yet.' BOTH MESSAGES ALREADY
 * EXIST IN THE PRODUCT'S TRANSLATIONS AND ARE NEVER SHOWN, because the history
 * is never empty in the demo. A build must wire them."
 */
function History({
  title,
  runs,
  empty,
}: {
  title: string;
  runs: readonly TransferRun[];
  empty: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <div className="grid gap-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {runs.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="grid gap-1.5">
          {runs.map((run) => (
            <li key={run.id} className="flex flex-wrap items-baseline gap-2 text-xs">
              <span className="font-medium">{t(`impexp.type.${run.dataType}`)}</span>
              <span className="tabular-nums text-muted-foreground">
                {run.format ? `${run.format.toUpperCase()} · ` : ''}
                {t('impexp.rows', { count: run.rowCount })} ·{' '}
                {new Date(run.ranAt).toLocaleDateString(i18n.language)}
              </span>
              {/* BR-ADM-03-07 / EC-15-17 — the person, which the prototype
                  stores and never shows. An em dash when they have left. */}
              <span className="text-muted-foreground">{run.ranBy ?? '—'}</span>
              {run.direction === 'import' ? (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px]',
                    run.status === 'imported'
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                      : run.status === 'warnings'
                        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
                        : run.status === 'failed'
                          ? 'bg-red-500/10 text-red-700 dark:text-red-400'
                          : 'bg-muted text-muted-foreground',
                  )}
                >
                  {t(`impexp.status.${run.status}`)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The grid becomes a file. Column LABELS are translated here — the server
 * sends stable machine keys, so an Arabic user's sheet is Arabic and a heading
 * a translator improves never changes what a future import will accept.
 */
function writeFile(
  grid: TransferGrid,
  format: ExportFormat,
  t: TFunction,
  suffix = 'export',
): void {
  const headers = grid.columns.map((column) => t(`impexp.col.${column.key}`));
  const name = `${grid.dataType}-${suffix}-${new Date().toISOString().slice(0, 10)}`;

  if (format === 'csv') {
    downloadCsv(`${name}.csv`, headers, grid.rows);
    return;
  }
  downloadXlsx(`${name}.xlsx`, t(`impexp.type.${grid.dataType}`), headers, grid.rows, {
    // The two things a CSV cannot say. Without them a numeric column is text
    // and the accountant's SUM returns zero.
    numeric: grid.columns.flatMap((column, index) => (column.kind === 'number' ? [index] : [])),
    dates: grid.columns.flatMap((column, index) => (column.kind === 'date' ? [index] : [])),
  });
}
